import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { scrollToId } from "./Nav";
import pages from "@/seo/pages.json";

const SOCIALS = [
  { id: "facebook", label: "Facebook", url: "https://www.facebook.com/profile.php?id=61579587810996" },
  { id: "linkedin", label: "LinkedIn", url: "https://www.linkedin.com/in/chris-dickson-138225380/" },
  { id: "tiktok", label: "TikTok", url: "https://www.tiktok.com/@launchpadtv5" },
  { id: "x", label: "X", url: "https://x.com/launchpad_tv3" },
  { id: "youtube", label: "YouTube", url: "https://www.youtube.com/@Launchpad_TV" },
  { id: "reddit", label: "Reddit", url: "https://www.reddit.com/user/LAUNCHPAD-TV" },
  { id: "twitch", label: "Twitch", url: "https://www.twitch.tv/launchyou" },
  { id: "rumble", label: "Rumble", url: "https://rumble.com/user/LaunchpadTV" },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-white/10 bg-charcoal/60 px-4 sm:px-8 lg:px-16 pt-16 pb-10 overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20 text-center"
          data-testid="footer-closing-statement"
        >
          <motion.img
            src="/logo-symbol.webp"
            width={512}
            height={512}
            alt="Launchpad TV rocket symbol"
            className="h-20 w-auto mx-auto mb-8 drop-shadow-[0_0_36px_rgba(63,169,245,0.4)]"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
          <p className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl leading-tight text-white">
            The world doesn't need more gatekeepers.
          </p>
          <p className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl leading-tight text-white mt-2">
            It needs more launchpads.
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row justify-between gap-12">
          <div className="max-w-md">
            <button
              data-testid="footer-brand"
              onClick={() => scrollToId("#top")}
              className="flex items-center gap-3"
            >
              <img
                src="/logo-lockup.svg"
                alt="Launchpad TV"
                width={850}
                height={74}
                className="w-[260px] sm:w-[320px] h-auto object-contain drop-shadow-[0_0_20px_rgba(63,169,245,0.45)]"
              />
            </button>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed">
              Launch where you land. Launchpad TV is a creator-first connected TV network connecting
              creators and communities with global audiences through discovery, streaming, and culture.
            </p>
            <p className="mt-6 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-600">
              In association with Virgin Music Group · Universal Music Group
            </p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-600" data-testid="footer-address">
              USA Location — 235 Peach Tree St. Atlanta, GA 30303
            </p>
          </div>

          <div className="grid grid-cols-2 gap-12">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-600 mb-4">
                Signal
              </div>
              {[
                { label: "Manifesto", href: "#manifesto", testid: "footer-link-manifesto" },
                { label: "Reach", href: "#reach", testid: "footer-link-reach" },
                { label: "Launchpad", href: "#launchpad", testid: "footer-link-launchpad" },
                { label: "Trailer", href: "#trailer", testid: "footer-link-trailer" },
                { label: "FAQ", href: "#faq", testid: "footer-link-faq" },
              ].map((l) => (
                <button
                  key={l.href}
                  data-testid={l.testid}
                  onClick={() => scrollToId(l.href)}
                  className="block font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-signal transition-colors duration-300 py-2.5 sm:py-1.5"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-4 pt-4 border-t border-white/5 font-mono text-[10px] tracking-[0.3em] uppercase text-slate-600 mb-2">
                For
              </div>
              {pages.map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}/`}
                  data-testid={`footer-link-${p.slug}`}
                  className="block font-mono text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-signal transition-colors duration-300 py-2.5 sm:py-1.5"
                >
                  {p.navLabel}
                </Link>
              ))}
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-600 mb-4">
                Follow the Signal
              </div>
              <div className="grid grid-cols-4 gap-2 max-w-[240px]">
                {SOCIALS.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${s.id}`}
                    aria-label={`Launchpad TV on ${s.label}`}
                    title={s.label}
                    className="group flex items-center justify-center w-12 h-12 border border-white/10 glass-panel hover:border-signal/70 hover:-translate-y-0.5 transition-[border-color,transform] duration-300"
                  >
                    <img
                      src={`/social/${s.id}.svg`}
                      alt=""
                      className="w-5 h-5 opacity-90 transition-opacity duration-300 group-hover:opacity-100 group-hover:drop-shadow-[0_0_8px_rgba(63,169,245,0.9)]"
                    />
                  </a>
                ))}
              </div>
              <a
                href="mailto:info@launchpad-tv.com"
                data-testid="footer-email"
                className="mt-4 inline-block font-mono text-[10px] tracking-[0.05em] sm:tracking-[0.15em] uppercase text-slate-500 hover:text-signal transition-colors duration-300 break-all py-2"
              >
                info@launchpad-tv.com
              </a>
            </div>
          </div>
        </div>

        <div
          id="partners"
          className="mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6"
          data-testid="footer-partners"
        >
          <div className="max-w-md text-center sm:text-left">
            <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-600">
              Partners
            </h2>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed" data-testid="footer-partners-copy">
              Launchpad TV works with channel partners, FAST distributors and technology partners to
              carry creator-first programming into connected TV homes worldwide.
            </p>
          </div>
          <div className="flex items-center gap-10 sm:gap-14">
            <img
              src="/partners/nexus.png"
              alt="Launchping Nexus — Connect Where You Land"
              width={1040}
              height={196}
              loading="lazy"
              data-testid="partner-nexus"
              className="h-9 sm:h-11 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
            />
            <a href="https://thetoneknows.com/" target="_blank" rel="noopener noreferrer" aria-label="Tone — Audio Activated QR website">
              <img
                src="/partners/tone.png"
                alt="Tone — Audio Activated QR"
                width={241}
                height={81}
                loading="lazy"
                data-testid="partner-tone"
                className="h-9 sm:h-11 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
              />
            </a>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left pb-[env(safe-area-inset-bottom)]">
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-600">
            © 2026 Launchpad-TV — A venture of MC Films Group × AngelDove Global Media
          </span>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-600">
            CH 001 // SIGNAL ACQUISITION IN PROGRESS
          </span>
        </div>
      </div>
    </footer>
  );
}
